import { useEffect, useRef, useState } from 'react';

interface PageTransitionProps {
  label?: string;
}

export default function PageTransition({ label }: PageTransitionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="w-full bg-white py-10 md:py-14 px-8 md:px-16 lg:px-24 relative overflow-hidden"
    >
      {/* Subtle background texture — warm tint */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F7] via-white to-white pointer-events-none" />

      <div className="relative max-w-6xl mx-auto">
        {/* Top decorative line — fades in from center */}
        <div className="flex items-center justify-center mb-6">
          <div
            className={`h-px bg-gradient-to-r from-transparent via-[#C9A961]/60 to-transparent transition-all duration-1000 ease-out ${
              visible ? 'w-24 opacity-100' : 'w-0 opacity-0'
            }`}
          />
        </div>

        {/* Optional label — small caps, centered */}
        {label && (
          <div
            className={`text-center mb-4 transition-all duration-700 ease-out ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            <span className="text-[#9A9A9A] text-[10px] uppercase tracking-[0.35em] font-medium">
              {label}
            </span>
          </div>
        )}

        {/* Large decorative mark — the bridge between dark hero and light content */}
        <div
          className={`flex items-center justify-center transition-all duration-700 ease-out ${
            visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
          style={{ transitionDelay: '250ms' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 md:w-20 h-px bg-gradient-to-r from-transparent to-[#E0DDD9]" />
            <div className="w-2 h-2 rounded-full border border-[#C9A961]/40" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#C9A961]/20" />
            <div className="w-2 h-2 rounded-full border border-[#C9A961]/40" />
            <div className="w-12 md:w-20 h-px bg-gradient-to-l from-transparent to-[#E0DDD9]" />
          </div>
        </div>

        {/* Bottom hairline — full width, very subtle */}
        <div
          className={`mt-10 h-px bg-[#F0EDE8] transition-all duration-1000 ease-out ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '400ms' }}
        />
      </div>
    </div>
  );
}
